import { useState } from "react";

import { get, map } from "lodash";

import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from "@mui/icons-material";
import {
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  TextField,
  Typography,
} from "@mui/material";

import { IStaffJob } from "@/api";

import { Autocomplete, Checkbox } from "../TrackedComponents";

import { useGetOptionsAndSelection, useManageGroups } from "./hooks";
import { useOpenState } from "./hooks/useOpenState";
import { TAutocompleteOption, TStaffJobPickerProps } from "./types";

import "./StaffJobPicker.scss";

export const StaffJobPicker = <
  TMultiple extends boolean,
  TValue extends TMultiple extends true ? IStaffJob["id"][] : IStaffJob["id"],
  TOption extends TMultiple extends true ? TAutocompleteOption[] : TAutocompleteOption,
>(
  props: TStaffJobPickerProps<TMultiple, TValue>,
) => {
  // Handle options and selected value(s)
  const { options, selection } = useGetOptionsAndSelection(props);

  // Handle grouping and group state + management
  const { groupSelectionState, onGroupSelect } = useManageGroups(props, options);

  // Handle custom state due to limit-tags feature bug
  const openStateParams = useOpenState();

  if (options.length === 0) return null;

  return (
    <Autocomplete<TAutocompleteOption, TMultiple, TMultiple extends true ? false : true, false>
      trackingLabel="staff-job-picker"
      {...openStateParams}
      /*
       ** Propagate some props to the Autocomplete component as is
       */
      readOnly={props.readOnly}
      placeholder={props.label}
      multiple={props.multiple}
      limitTags={props.limitTags}
      size={props.size}
      disableClearable={!props.multiple as TMultiple extends true ? false : true}
      /*
       ** Class names
       */
      className="m7-staff-job-picker"
      classes={{
        popper: "m7-staff-job-picker-popper",
      }}
      /*
       ** Options, values, and organization/display
       */
      options={options}
      value={selection ?? null}
      groupBy={(option) => option.staffCategory?.name ?? ""}
      getOptionLabel={(option) => option.label}
      /*
       ** On change + search
       */
      onChange={(_, optionOrOptions) => {
        if (props.multiple) {
          props.onChange?.(map(optionOrOptions as TOption[], "value.id") as TValue);
        } else {
          props.onChange?.(get(optionOrOptions as TOption, "value.id") as TValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={props.size}
          label={props.label ?? (props.multiple ? "Staff Jobs" : "Staff Jobs")}
        />
      )}
      /*
       ** Render Specific "MULTI SELECT" props
       */
      {...(props.multiple
        ? {
            disableCloseOnSelect: true,
            renderOption: OptionElement,
            renderGroup: (params) => (
              <GroupElement
                key={params.key}
                onGroupSelect={onGroupSelect}
                groupSelectionState={groupSelectionState}
                params={params}
              />
            ),
          }
        : {})}
    />
  );
};

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;
const indeterminateIcon = <IndeterminateCheckBox fontSize="small" />;

/**
 * Takes the props of the autocomplete "renderOption" and renders the option element:
 * We have a checkbox and the option label.
 * Plus some custom logic to handle the focused class.
 */
const OptionElement = (
  { key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: string },
  option: TAutocompleteOption,
  { selected }: AutocompleteRenderOptionState,
) => (
  <li
    {...optionProps}
    key={key}
    onClick={(event) => {
      event.stopPropagation();
      optionProps.onClick?.(event);
    }}
    // Force on mouse leave because mouse leaving for a group element
    //  does't remove the focused class for the option element
    onMouseLeave={(event: React.MouseEvent<HTMLLIElement>) => {
      event.currentTarget.className = event.currentTarget.className.replace("Mui-focused", "");
      optionProps.onMouseLeave?.(event);
    }}
  >
    <Checkbox
      trackingLabel={`select-staff-job`}
      icon={icon}
      checkedIcon={checkedIcon}
      style={{ marginRight: 8 }}
      checked={selected}
    />
    {option.label}
  </li>
);

/**
 * Takes the props of the autocomplete "renderGroup" and renders the group element:
 * We have a checkbox and the group label.
 * Plus some custom logic to handle the checkbox state and the focused class.
 */
const GroupElement = ({
  onGroupSelect,
  groupSelectionState,
  params,
}: {
  onGroupSelect: (groupName: string) => void;
  groupSelectionState: (groupName: string) => boolean | undefined;
  params: AutocompleteRenderGroupParams;
}) => {
  const [focused, setFocused] = useState(false);
  const groupState = groupSelectionState(params.group);
  const groupOptionLabelClassName = (() => {
    const classes = ["MuiAutocomplete-option", "group-option-label"];

    if (groupState === true) classes.push("group-option-selected");
    else if (groupState === false) classes.push("group-option-unselected");
    else if (groupState === undefined) classes.push("group-option-indeterminate");

    if (focused) classes.push("Mui-focused");

    return classes.join(" ");
  })();

  return (
    <li className={"group-option"} key={params.key} onClick={() => onGroupSelect(params.group)}>
      <div
        className={groupOptionLabelClassName}
        aria-selected={groupState !== false}
        onMouseEnter={() => setFocused(true)}
        onMouseLeave={() => setFocused(false)}
      >
        <Checkbox
          trackingLabel="select-staff-job-picker-group"
          icon={icon}
          checkedIcon={checkedIcon}
          indeterminateIcon={indeterminateIcon}
          checked={groupState === true}
          indeterminate={groupState === undefined}
          onClick={(event) => {
            event.stopPropagation();
            onGroupSelect(params.group);
          }}
        />
        <Typography>{params.group}</Typography>
      </div>
      <ul className="group-options">{params.children}</ul>
    </li>
  );
};
